import * as Yup from "yup";
import { CreatePatientDto, PatientFormShape } from "@containers/Patient/interfaces";
import {
  FORM_ERROR_MESSAGES,
  STANDARD_DIFFERENCE_IN_DAYS,
  STATUS,
  TIMEZONE,
  TRACKING_PARAMETER_SETTINGS,
} from "@shared/constants";
import { Option } from "@shared/interfaces";
import { Hospital, Patient, PatientSettings, Station, User } from "@shared/models";
import { actions as userActions, selectors as userSelectors } from "@containers/User/store/";
import { addDaysToDate, getDifferenceInDays, getFullName, getStationTrackingTimeRange } from "@shared/utils";
import { AGE_REGEXP, FEV1_REGEXP, PHONE_NUMBER_REGEXP } from "@shared/constants/regexpMatches";

const patientEmailValidationSchema = Yup.string()
  .email(FORM_ERROR_MESSAGES.EMAIL)
  .when("phone", {
    is: (phone: string) => !phone || phone.length === 0,
    then: Yup.string()
      .email(FORM_ERROR_MESSAGES.EMAIL)
      .max(150, `Email ${FORM_ERROR_MESSAGES.LONG}`)
      .required("Please add the phone number or email address for client creation."),
    otherwise: Yup.string(),
  });

const patientPhoneValidationSchema = Yup.string().when("email", {
  is: (email: string) => !email || email.length === 0,
  then: Yup.string()
    .min(10, `Phone ${FORM_ERROR_MESSAGES.SHORT}`)
    .matches(PHONE_NUMBER_REGEXP, FORM_ERROR_MESSAGES.PHONE_NUMBER)
    .required("Please add the phone number or email address for client creation."),
  otherwise: Yup.string(),
});

const patientStartEndDateValidationSchema = Yup.array()
  .typeError(FORM_ERROR_MESSAGES.REQUIRED)
  .test("start_end_date", "Data range should be 14 days or less", function (value) {
    if (this.parent.tracking_parameter_settings.value !== TRACKING_PARAMETER_SETTINGS[0].value) {
      return true;
    }

    if (value && value.length) {
      const differenceInDays = getDifferenceInDays(new Date(value[0]), new Date(value[1]));
      if (differenceInDays > STANDARD_DIFFERENCE_IN_DAYS) {
        return false;
      }
    }

    return true;
  });

const patientHrStableValidationSchema = Yup.string().when("tracking_parameter_settings", {
  is: (tracking_parameter_settings: Option) =>
    tracking_parameter_settings.value === TRACKING_PARAMETER_SETTINGS[1].value,
  then: Yup.string()
    .matches(AGE_REGEXP, `HR for Stable Period ${FORM_ERROR_MESSAGES.NUMBER}`)
    .max(3, `HR for Stable Period ${FORM_ERROR_MESSAGES.LONG}`)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
});

const patientRrStableValidationSchema = Yup.string().when("tracking_parameter_settings", {
  is: (tracking_parameter_settings: Option) =>
    tracking_parameter_settings.value === TRACKING_PARAMETER_SETTINGS[1].value,
  then: Yup.string()
    .matches(AGE_REGEXP, `PR for Stable Period ${FORM_ERROR_MESSAGES.NUMBER}`)
    .max(3, `PR for Stable Period ${FORM_ERROR_MESSAGES.LONG}`)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
});

const optionValidationSchema = Yup.object().shape({
  value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
  label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
});

export const validationSchema: Yup.SchemaOf<PatientFormShape> = Yup.object().shape(
  {
    first_name: Yup.string().max(35, `First Name ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
    last_name: Yup.string().max(35, `Last Name ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
    patient_code: Yup.string().max(35, `Patient ID ${FORM_ERROR_MESSAGES.LONG}`).optional(),
    login: Yup.string().max(35, `Login Username ${FORM_ERROR_MESSAGES.LONG}`).optional(),
    gender: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED).nullable(),
    enrollment_type: Yup.string().optional().nullable(),
    gold_stage: Yup.string().optional().nullable(),
    description: Yup.string().optional().nullable(),
    age: Yup.string()
      .matches(AGE_REGEXP, `Age ${FORM_ERROR_MESSAGES.NUMBER}`)
      .max(3, `Age ${FORM_ERROR_MESSAGES.LONG}`)
      .required(FORM_ERROR_MESSAGES.REQUIRED),
    fev1: Yup.string().matches(FEV1_REGEXP, `FEV1 must be between 0 and 100`),
    most_recent_exacerbation_completed_at: Yup.date().nullable(),
    assigned_users: Yup.array()
      .of(optionValidationSchema)
      .min(1, "This field must contain at least 1 Staff")
      .required(FORM_ERROR_MESSAGES.REQUIRED),
    email: patientEmailValidationSchema,
    phone: patientPhoneValidationSchema,
    station_start_time: Yup.date().nullable(),
    station_end_time: Yup.date().nullable(),
    station_timezone: Yup.string().nullable(),
    timezone: Yup.string().typeError(FORM_ERROR_MESSAGES.REQUIRED).required(FORM_ERROR_MESSAGES.REQUIRED),
    tracking_parameter_settings: optionValidationSchema,
    start_end_date: patientStartEndDateValidationSchema,
    hr_stable: patientHrStableValidationSchema,
    rr_stable: patientRrStableValidationSchema,
  },
  [["email", "phone"]],
);

export const getHandlers = (hospital: Hospital) => {
  return {
    assigned_users: {
      getData: (search: string) =>
        userActions.getUsers.request({
          search,
          page: 0,
          limit: 20,
          status: STATUS.ACTIVE,
          hospital: { value: String(hospital.id), label: hospital.name },
        }),
      selectData: userSelectors.getUsers,
      prepareOptionFunction: (item: User): Option => {
        return { label: getFullName(item), value: String(item.id) };
      },
      loadingAction: userActions.getUsers,
    },
  };
};

const getInitialDateRange = (startDate: Date | string, endDate: Date | string): [Date, Date] => [
  parseLocalDate(startDate),
  parseLocalDate(endDate),
];

export const getInitValues = (
  patient: Patient | null,
  authorizedUser: User,
  canViewUsersList: boolean,
): PatientFormShape => {
  const stations = patient?.stations || [];
  const station: Station | null = stations.find(Boolean) || null;

  const { endTime, startTime, timezone } = getStationTrackingTimeRange(station);

  return {
    first_name: patient?.first_name || "",
    last_name: patient?.last_name || "",
    patient_code: patient?.user_code || "",
    login: patient?.login || "",
    gender: patient?.gender || "",
    age: patient?.age ? String(patient?.age) : "",
    phone: patient?.phone || "",
    email: patient?.email || "",
    timezone: patient?.patient_settings?.timezone || TIMEZONE.EUROPE,
    station_start_time: startTime,
    station_end_time: endTime,
    station_timezone: timezone,
    hr_stable: patient?.patient_settings?.hr_stable_period ? String(patient.patient_settings.hr_stable_period) : "",
    rr_stable: patient?.patient_settings?.rr_stable_period ? String(patient.patient_settings.rr_stable_period) : "",
    tracking_parameter_settings: getInitTrackingParameterSettingsValues(patient),
    start_end_date: getInitStartEndDate(patient),
    assigned_users: getInitAssignedUsers(patient, authorizedUser, canViewUsersList),
  };
};

const getInitAssignedUsers = (patient: Patient | null, authorizedUser: User, canViewUsersList: boolean) => {
  if (!canViewUsersList) return [{ value: String(authorizedUser.id), label: getFullName(authorizedUser) }];

  return (patient?.assigned_users || []).map((user) => {
    return {
      value: String(user.id),
      label: getFullName(user),
    };
  });
};

const getInitStartEndDate = (patient: Patient | null) => {
  return patient?.patient_settings?.start_date && patient?.patient_settings?.end_date
    ? getInitialDateRange(patient.patient_settings.start_date, patient.patient_settings.end_date)
    : getInitialDateRange(new Date(), addDaysToDate(new Date(), STANDARD_DIFFERENCE_IN_DAYS));
};

const getInitTrackingParameterSettingsValues = (patient: Patient | null) => {
  if (!patient?.patient_settings?.tracking_parameters) return TRACKING_PARAMETER_SETTINGS[0];

  const value = patient.patient_settings.tracking_parameters;
  let label = patient.patient_settings.tracking_parameters;
  if (patient.patient_settings.tracking_parameters === TRACKING_PARAMETER_SETTINGS[1].value) {
    label = TRACKING_PARAMETER_SETTINGS[1].label;
  }

  return {
    value,
    label,
  };
};

export const prepareSubmitValue = (value: PatientFormShape, hospital: Hospital | null): CreatePatientDto => {
  const patientSettings: Partial<PatientSettings> = {
    timezone: value.timezone,
    tracking_parameters: value.tracking_parameter_settings.value,
    start_date: value.start_end_date ? dateChangeHandler(value.start_end_date).convertedStartDate : null,
    end_date: value.start_end_date ? dateChangeHandler(value.start_end_date).convertedEndDate : null,
    hr_stable_period: value.hr_stable ? Number(value.hr_stable) : null,
    rr_stable_period: value.rr_stable ? Number(value.rr_stable) : null,
  };

  return {
    first_name: value.first_name,
    last_name: value.last_name,
    patient_code: value.patient_code || null,
    login: value.login || null,
    gender: value.gender,
    age: Number(value.age),
    phone: value.phone || null,
    email: value.email || null,
    assigned_users: value.assigned_users.map((opt) => Number(opt.value)),
    patient_settings: patientSettings,
    hospital_id: hospital ? hospital.id : undefined,
  };
};

const parseLocalDate = (date: Date | string): Date => {
  if (date instanceof Date) return date;
  // Parse date string as local time to avoid timezone shift
  // e.g., "2024-01-15" should be Jan 15 in local timezone, not UTC
  const [year, month, day] = date.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDateAsLocalString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dateChangeHandler = (date: [Date, Date]) => {
  const [startDate, endDate] = date;
  const convertedStartDate = `${formatDateAsLocalString(startDate)} 00:00:00`;
  const convertedEndDate = `${formatDateAsLocalString(endDate)} 23:59:59`;
  return { convertedStartDate, convertedEndDate };
};
